/**
 * Random rule, or checkboxes for the rules to pin from one group.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo, useRef, useState, type FC } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';
import { useScrollIsolation } from '@/components/ui/form/input/useScrollIsolation';

interface Rule { id: string; name: string }

export const RuleSelect: FC<{
  rules: Rule[];
  ruleIds: string[];
  onChange: (ruleIds: string[]) => void;
}> = ({ rules, ruleIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const shown = useMemo(() => filterRules(rules, search), [rules, search]);
  return (
    <Popover.Root open={open} onOpenChange={(next) => { setOpen(next); if (!next) setSearch(''); }}>
      <RuleTrigger open={open} label={ruleLabel(rules, ruleIds)} />
      <RuleMenu search={search} onSearch={setSearch} rules={shown} ruleIds={ruleIds} onChange={onChange} />
    </Popover.Root>
  );
};

const RuleTrigger: FC<{ open: boolean; label: string }> = ({ open, label }) => (
  <Popover.Trigger asChild>
    <button
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="min-w-0 truncate">{label}</span>
      <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
    </button>
  </Popover.Trigger>
);

const RuleMenu: FC<{
  search: string;
  onSearch: (v: string) => void;
  rules: Rule[];
  ruleIds: string[];
  onChange: (ruleIds: string[]) => void;
}> = ({ search, onSearch, rules, ruleIds, onChange }) => (
  <Popover.Portal>
    <Popover.Content
      align="start"
      sideOffset={6}
      className="z-[250] w-[max(280px,var(--radix-popover-trigger-width))] rounded-lg border border-border bg-popover p-0 text-popover-foreground shadow-md"
      onCloseAutoFocus={(e) => e.preventDefault()}
    >
      <RuleSearch search={search} onSearch={onSearch} />
      <RuleList rules={rules} ruleIds={ruleIds} showRandom={matchesRandom(search)} onChange={onChange} />
    </Popover.Content>
  </Popover.Portal>
);

function filterRules(rules: Rule[], search: string): Rule[] {
  const q = search.trim().toLowerCase();
  if (!q) return rules;
  return rules.filter((rule) => rule.name.toLowerCase().includes(q));
}

function matchesRandom(search: string): boolean {
  const q = search.trim().toLowerCase();
  return !q || 'random rule'.includes(q);
}

function ruleLabel(rules: Rule[], ruleIds: string[]): string {
  if (ruleIds.length === 0) return 'Random rule';
  if (ruleIds.length === 1) return rules.find((rule) => rule.id === ruleIds[0])?.name ?? '1 rule';
  return `${ruleIds.length} rules`;
}

const RuleSearch: FC<{ search: string; onSearch: (v: string) => void }> = ({ search, onSearch }) => (
  <div className="border-b border-border p-2">
    <div className="flex min-w-0 items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
      <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      <input
        type="search"
        placeholder="Search rules…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        autoFocus
        autoComplete="off"
      />
    </div>
  </div>
);

const RuleList: FC<{
  rules: Rule[];
  ruleIds: string[];
  showRandom: boolean;
  onChange: (ruleIds: string[]) => void;
}> = ({ rules, ruleIds, showRandom, onChange }) => {
  const listRef = useRef<HTMLDivElement>(null);
  const setListRef = useScrollIsolation(listRef);
  const random = ruleIds.length === 0;
  return (
    <div ref={setListRef} role="listbox" aria-multiselectable className="max-h-[320px] overflow-y-auto p-2">
      {showRandom ? <CheckRow label="Random rule" on={random} onClick={() => onChange([])} /> : null}
      {rules.map((rule) => (
        <CheckRow
          key={rule.id}
          label={rule.name}
          on={!random && ruleIds.includes(rule.id)}
          onClick={() => onChange(toggleRule(ruleIds, rule.id))}
        />
      ))}
    </div>
  );
};

function toggleRule(ruleIds: string[], id: string): string[] {
  return ruleIds.includes(id) ? ruleIds.filter((ruleId) => ruleId !== id) : [...ruleIds, id];
}

const CheckRow: FC<{ label: string; on: boolean; onClick: () => void }> = ({ label, on, onClick }) => (
  <button
    type="button"
    role="option"
    aria-selected={on}
    onClick={onClick}
    className={cn('flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-accent', on && 'bg-accent/60')}
  >
    <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', on ? 'border-primary bg-primary text-primary-foreground' : 'border-input')} aria-hidden>
      {on ? <Check className="h-3 w-3" /> : null}
    </span>
    <span className="min-w-0 truncate">{label}</span>
  </button>
);
